import { useEffect, useState } from "react";
import { useAuth, formatApiError } from "@/lib/auth";
import { fetchCategories, fetchProducts } from "@/lib/api";
import { supabase, getStorageUrl } from "@/lib/supabase";
import { Plus, Pencil, Trash2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { CategoryForm } from "./CategoriesTab";

const EMPTY = {
  name: "", category: "t-shirts", price: 0, original_price: "", description: "",
  images: [], sizes: "", colors: "", badge: "", is_featured: false,
};

export default function ProductsTab() {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);  // null | EMPTY | product
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([fetchCategories(), fetchProducts()]);
      setCats(c);
      setProducts(p);
    } catch (e) {
      toast.error("Failed to load: " + (e.message || ""));
    }
  };
  useEffect(() => { load(); }, []);

  const visible = filter ? products.filter(p => p.category === filter) : products;

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(formatApiError(e) || e.message); }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Products ({products.length})</h2>
          <p className="text-black/70">Add, edit, or remove items shown on the site.</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="add-product-btn"
          className="nb-btn bg-[var(--primary)] text-white"
        ><Plus className="inline mr-1" size={18} strokeWidth={3} /> Add Product</button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <button onClick={() => setFilter("")} className={`nb-pill ${!filter ? "bg-[var(--primary)] text-white" : ""}`}>All</button>
        {cats.map(c => (
          <button key={c.slug} onClick={() => setFilter(c.slug)} className={`nb-pill ${filter === c.slug ? "bg-[var(--primary)] text-white" : ""}`}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((p) => (
          <div key={p.id} className="nb-card p-4 flex gap-3" data-testid={`admin-product-${p.id}`}>
            <div className="w-20 h-20 shrink-0 rounded-2xl border-[3px] border-black overflow-hidden bg-[var(--muted)]">
              {p.images?.[0]
                ? <img src={resolveImg(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                : <div className="flex items-center justify-center w-full h-full"><ImageIcon /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate">{p.name}</div>
              <div className="text-xs uppercase tracking-wider text-black/60">{p.category}</div>
              <div className="font-display font-black">₹{p.price}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(toForm(p))} className="nb-pill !text-xs !py-1.5" data-testid={`edit-product-${p.id}`}><Pencil size={12} strokeWidth={3} className="inline mr-1" />Edit</button>
                <button onClick={() => remove(p.id)} className="nb-pill !text-xs !py-1.5 bg-black text-white" data-testid={`delete-product-${p.id}`}><Trash2 size={12} strokeWidth={3} className="inline mr-1" />Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ProductForm
          cats={cats}
          initial={editing}
          busy={busy}
          setBusy={setBusy}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onRefreshCats={async () => {
            const c = await fetchCategories();
            setCats(c);
            return c;
          }}
        />
      )}
    </div>
  );
}

// Helpers — convert backend product → form state and back.
function toForm(p) {
  return {
    ...p,
    original_price: p.original_price ?? "",
    badge: p.badge ?? "",
    sizes: (p.sizes || []).join(", "),
    colors: (p.colors || []).join(", "),
  };
}
function fromForm(f) {
  return {
    name: f.name.trim(),
    category: f.category,
    price: parseInt(f.price, 10) || 0,
    original_price: f.original_price ? parseInt(f.original_price, 10) : null,
    description: f.description || "",
    images: f.images || [],
    sizes: f.sizes ? f.sizes.split(",").map(s => s.trim()).filter(Boolean) : [],
    colors: f.colors ? f.colors.split(",").map(s => s.trim()).filter(Boolean) : [],
    badge: f.badge || null,
    is_featured: !!f.is_featured,
  };
}

export function resolveImg(url) {
  return getStorageUrl(url);
}

function ProductForm({ cats, initial, busy, setBusy, onClose, onSaved, onRefreshCats }) {
  const [f, setF] = useState(initial);
  const [showAddCat, setShowAddCat] = useState(false);
  const [catBusy, setCatBusy] = useState(false);
  const isEdit = !!f.id;

  const upload = async (file) => {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { data, error } = await supabase.storage.from("images").upload(`products/${fileName}`, file);
    if (error) throw error;
    return data.path;
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        urls.push(await upload(file));
      }
      setF((cur) => ({ ...cur, images: [...(cur.images || []), ...urls] }));
      toast.success(`Uploaded ${urls.length} image${urls.length>1?"s":""}`);
    } catch (e2) {
      toast.error(formatApiError(e2) || e2.message);
    } finally { setBusy(false); }
  };

  const removeImg = (idx) => setF(c => ({ ...c, images: c.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!f.name || !f.category || !f.price) {
      toast.error("Name, category and price are required");
      return;
    }
    setBusy(true);
    try {
      const body = fromForm(f);
      if (isEdit) {
        const { error } = await supabase.from("products").update(body).eq("id", f.id);
        if (error) throw error;
        toast.success("Product updated");
      } else {
        const { error } = await supabase.from("products").insert([body]);
        if (error) throw error;
        toast.success("Product created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="nb-card bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black uppercase text-2xl">{isEdit ? "Edit" : "Add"} Product</h3>
          <button onClick={onClose} className="nb-pill" data-testid="form-close"><X size={14} strokeWidth={3} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name *">
            <input value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="form-name" className="form-input" />
          </Field>
          <Field label="Category *">
            <div className="flex gap-2">
              <select value={f.category} onChange={e=>setF({...f, category: e.target.value})} data-testid="form-category" className="form-input flex-1">
                {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowAddCat(true)}
                className="w-[45px] h-[45px] flex items-center justify-center border-[3px] border-black rounded-full bg-white text-black hover:bg-[var(--secondary)] active:translate-y-0.5 shadow-[3px_3px_0_0_#0A0A0A] hover:shadow-[4px_4px_0_0_#0A0A0A] transition-all shrink-0"
                title="Add New Category"
              >
                <Plus size={18} strokeWidth={3} />
              </button>
            </div>
          </Field>
          <Field label="Price (₹) *">
            <input type="number" value={f.price} onChange={e=>setF({...f, price: e.target.value})} data-testid="form-price" className="form-input" />
          </Field>
          <Field label="Original price (₹) — optional">
            <input type="number" value={f.original_price} onChange={e=>setF({...f, original_price: e.target.value})} data-testid="form-orig-price" className="form-input" />
          </Field>
          <Field label="Sizes (comma separated)">
            <input value={f.sizes} onChange={e=>setF({...f, sizes: e.target.value})} placeholder="S, M, L, XL" data-testid="form-sizes" className="form-input" />
          </Field>
          <Field label="Colors (comma separated)">
            <input value={f.colors} onChange={e=>setF({...f, colors: e.target.value})} placeholder="Pink, Black" data-testid="form-colors" className="form-input" />
          </Field>
          <Field label="Badge">
            <select value={f.badge || ""} onChange={e=>setF({...f, badge: e.target.value})} data-testid="form-badge" className="form-input">
              <option value="">None</option>
              <option value="HOT">HOT</option>
              <option value="NEW">NEW</option>
              <option value="PREMIUM">PREMIUM</option>
            </select>
          </Field>
          <Field label="Featured?">
            <label className="flex items-center gap-2 mt-2">
              <input type="checkbox" checked={f.is_featured} onChange={e=>setF({...f, is_featured: e.target.checked})} data-testid="form-featured" className="w-5 h-5" />
              <span>Show in homepage featured grid</span>
            </label>
          </Field>
        </div>

        <Field label="Description">
          <textarea value={f.description} onChange={e=>setF({...f, description: e.target.value})} rows={3} data-testid="form-desc" className="form-input !rounded-2xl" />
        </Field>

        <Field label="Images (HD)">
          <div className="flex flex-wrap gap-3 mb-3">
            {(f.images || []).map((u, i) => (
              <div key={i} className="relative w-24 h-24 rounded-2xl border-[3px] border-black overflow-hidden">
                <img src={resolveImg(u)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeImg(i)} type="button" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black text-white border-2 border-white flex items-center justify-center" data-testid={`form-remove-img-${i}`}>
                  <X size={12} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
          <label className="nb-pill cursor-pointer inline-block">
            <input type="file" multiple accept="image/*" onChange={onUpload} className="hidden" data-testid="form-upload" />
            <ImageIcon size={14} strokeWidth={3} className="inline mr-1" /> Upload images
          </label>
          <p className="text-xs text-black/60 mt-2">JPG / PNG / WebP, up to 6MB each. Multiple files supported.</p>
        </Field>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="nb-pill" data-testid="form-cancel">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="form-save" className="nb-btn bg-[var(--primary)] text-white disabled:opacity-60">
            {busy ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
      </div>

      {showAddCat && (
        <CategoryForm
          initial={{ name: "", slug: "", image: "", accent: "#FF3366" }}
          busy={catBusy}
          setBusy={setCatBusy}
          onClose={() => setShowAddCat(false)}
          onSaved={async (newCat) => {
            setShowAddCat(false);
            if (onRefreshCats) {
              const updatedCats = await onRefreshCats();
              if (newCat?.slug) {
                setF(cur => ({ ...cur, category: newCat.slug }));
              }
            }
          }}
        />
      )}

      <style>{`
        .form-input { width:100%; padding: 0.55rem 1rem; border:3px solid #0A0A0A; border-radius: 9999px; box-shadow: 3px 3px 0 0 #0A0A0A; outline:none; background:#fff; }
        .form-input:focus { box-shadow: 4px 4px 0 0 #FF3366; }
        textarea.form-input { border-radius: 18px; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-sm font-black uppercase tracking-wider mb-1 inline-block">{label}</span>
      {children}
    </label>
  );
}
